import { useCallback, useMemo, useState } from 'react';

import { Box, Typography, useTheme } from '@mui/material';
import { useQuery } from '@tanstack/react-query';

import ChartShell from '@/components/Chart/ChartShell';
import useRegularChart from '@/components/Chart/RegularChart/useRegularChart';
import type { ExtendedOptions } from '@/components/Chart/types/highcharts';
import type { TimeRangeButton } from '@/constants/constants';
import {
  DEFAULT_CHART_HEIGHT,
  regularChartPalette,
  TABLE_ROW_HEIGHT_PX
} from '@/constants/constants';
import { Permission } from '@/entities/permissions';
import { chartOptions } from '@/features/Charts/api';
import useCsvDownload from '@/hooks/useCsvDownload';
import usePermission from '@/shared/hooks/usePermission';
import { EmptyState } from '@/shared/ui/feedback/EmptyState';
import Loader from '@/shared/ui/feedback/Spinner';
import TimeRangeButtons from '@/shared/ui/primitives/TimeRangeButtons';
import { deepMerge } from '@/utils/deepMerge';
import { detectEnvironment } from '@/utils/detectEnvironment';

import type Highcharts from 'highcharts/highstock';

const environment = detectEnvironment();
const GROUP_NAME = `Meta_Go_Config${environment === 'staging' ? '_Staging' : ''}`;

type LayoutProps = {
  fullWidth?: boolean;
  height?: number;
};

type CustomChartProps = {
  metadata?: Record<string, object | string>;
  layout?: LayoutProps;
};

const resolveSankeyTooltipFormatter = (tooltip?: Highcharts.TooltipOptions) => {
  if (!tooltip?.formatter) return;
  if (typeof tooltip.formatter === 'function') return;

  const rawFormatter = tooltip.formatter as unknown;
  if (typeof rawFormatter === 'string') {
    const formatterKey = rawFormatter.trim();
    if (formatterKey && !formatterKey.includes('function') && !formatterKey.includes('=>')) {
      tooltip.pointFormat = tooltip.pointFormat ?? formatterKey;
      delete tooltip.formatter;
      return;
    }
  }

  delete tooltip.formatter;
  console.warn('Invalid sankey tooltip formatter');
};

const Index = ({ metadata, layout }: CustomChartProps) => {
  const theme = useTheme();
  const { hasPermission } = usePermission();

  const seriesFileName = metadata?.seriesFileName as string;
  const configFileName = metadata?.configFileName as string;
  const queryFileId = metadata?.queryFileId as string;
  const downloadFileId = metadata?.downloadFileId as string;

  const timeRangeButtons = useMemo(
    () => (metadata?.timeRangeButtons as TimeRangeButton[] | undefined) ?? [],
    [metadata]
  );
  const getButtonIndex = useCallback(() => {
    return timeRangeButtons.findIndex(bt => bt.default);
  }, [timeRangeButtons]);
  const getFromTimeRangeButtons = useCallback(() => {
    const index = timeRangeButtons.findIndex(bt => bt.default);
    if (index > -1) {
      return timeRangeButtons[index].value;
    }
    return 'all';
  }, [timeRangeButtons]);

  const [from, setFrom] = useState<string | undefined>(getFromTimeRangeButtons());
  const [activeButtonIndex, setActiveButtonIndex] = useState(getButtonIndex());

  const { height } = layout || {};

  const {
    data: chartConfig,
    isError: isChartConfigError,
    isLoading: isChartConfigLoading
  } = useQuery(chartOptions.customConfig(configFileName, GROUP_NAME));
  const {
    data: chartSeries,
    isError: isChartSeriesError,
    isLoading: isChartSeriesLoading
  } = useQuery(
    chartOptions.customData({
      fileName: seriesFileName,
      groupName: GROUP_NAME,
      queryFileId,
      from
    })
  );
  useCsvDownload(from, downloadFileId);

  const { chartContainerRef } = useRegularChart({
    chartSeries
  });
  /** Build chart options */
  const resolvedChartOptions = useMemo<ExtendedOptions | null>(() => {
    if (!chartConfig || !chartSeries) return null;

    const chartHeight = height ? height * TABLE_ROW_HEIGHT_PX + 40 : DEFAULT_CHART_HEIGHT;

    const baseOptions: ExtendedOptions = {
      accessibility: { enabled: false },
      title: { text: undefined },
      colors: regularChartPalette,
      exporting: {
        buttons: { contextButton: { enabled: false } },
        scale: 1,
        enabled: true,
        filename: 'GX-Chart',
        chartOptions: {
          legend: {
            enabled: true,
            itemStyle: {
              color: theme.palette.gx.text,
              fontSize: '0.8em',
              textOverflow: 'ellipsis'
            }
          },
          scrollbar: { enabled: false }
        }
      },
      tooltip: { enabled: false },
      stockTools: { gui: { enabled: false } },
      legend: { enabled: true, itemStyle: { color: theme.palette.gx.text } },
      credits: { enabled: false },
      xAxis: { labels: { style: { color: theme.palette.gx.text } } },
      yAxis: {
        title: { text: '' },
        labels: { style: { color: theme.palette.gx.text } }
      },
      series: chartSeries,
      chart: {
        reflow: true,
        animation: false,
        backgroundColor: theme.palette.gx.tileDark,
        height: chartHeight,
        style: {
          color: theme.palette.gx.text,
          fontFamily: 'Cairo, sans-serif'
        }
      }
    };

    const merged = deepMerge(baseOptions, chartConfig);

    // Handle sankey formatter safely
    if (merged.chart?.type === 'sankey') {
      resolveSankeyTooltipFormatter(merged.tooltip);
    }
    return {
      ...merged,
      exporting: merged.exporting
        ? {
            pdfFont: {
              normal: 'https://www.highcharts.com/samples/data/fonts/NotoSans-Regular.ttf'
            },
            ...merged.exporting,
            chartOptions: {
              ...merged.exporting.chartOptions,
              chart: {
                ...merged.exporting.chartOptions?.chart,
                events: merged.exporting.chartOptions?.chart?.events
              }
            }
          }
        : merged.exporting
    };
  }, [chartConfig, chartSeries, height, theme]);

  const hasAnyData =
    Array.isArray(chartSeries) &&
    chartSeries.some(s => Array.isArray(s?.data) && s.data.length > 0);

  if (!hasPermission(Permission.ViewMarketScreens)) {
    return (
      <Box
        sx={{
          flex: 1,
          height: '200px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}
      >
        <Typography sx={{ textAlign: 'center' }}>
          You no longer have access.
          <br />
          Please upgrade to access the full chart.
        </Typography>
      </Box>
    );
  }

  /** Loading & Error states */
  if (isChartSeriesLoading || isChartConfigLoading) {
    return (
      <Box sx={{ height: DEFAULT_CHART_HEIGHT }}>
        <Loader />
      </Box>
    );
  }

  /** Render chart */
  if (resolvedChartOptions) {
    return (
      <Box
        ref={chartContainerRef}
        sx={{
          width: '100%',
          overflowX: 'hidden'
        }}
      >
        <Box className='base-chart'>
          {timeRangeButtons.length > 0 && (
            <TimeRangeButtons
              activeButtonIndex={activeButtonIndex}
              setActiveButtonIndex={setActiveButtonIndex}
              timeRangeButtons={timeRangeButtons}
              onButtonClick={setFrom}
            />
          )}
          {(isChartSeriesError || isChartConfigError) && <EmptyState />}
          {hasAnyData && (
            <ChartShell
              isStockChart={false}
              options={resolvedChartOptions}
            />
          )}
          {!hasAnyData && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '372px',
                textAlign: 'center',
                color: 'gx.textSecondary',
                flex: 1
              }}
            >
              <Typography variant='body1'>
                No data available for the selected date range. Try adjusting it.
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    );
  }

  return null;
};

export default Index;
